<template>
  <div class="production-timeline">
    <div class="timeline-legend">
      <span class="legend-item">
        <span class="legend-dot tl-bar--scheduled"></span>
        {{ $t("productionPriority.timelineScheduled") }}
      </span>
      <span class="legend-item">
        <span class="legend-dot tl-bar--draft"></span>
        {{ $t("productionPriority.timelineDraft") }}
      </span>
      <span class="legend-item">
        <span class="legend-dot tl-day-dot--all"></span>
        {{ $t("productionPriority.dayAll") }}
      </span>
      <span class="legend-item">
        <span class="legend-dot tl-day-dot--some"></span>
        {{ $t("productionPriority.daySome") }}
      </span>
      <span class="legend-item">
        <span class="legend-dot tl-day-dot--none"></span>
        {{ $t("productionPriority.dayNone") }}
      </span>
      <span class="legend-item">
        <span class="legend-dot tl-day-dot--holiday"></span>
        {{ $t("productionPriority.dayHoliday") }}
      </span>
    </div>

    <div class="timeline-scroll">
      <div v-for="board in boards" :key="board.column_key" class="tl-board">
        <div class="tl-board-header">
          <span class="tl-board-name" :title="board.name">{{
            board.name
          }}</span>
          <span class="badge" :class="boardBadgeClass(board.type)">
            {{ boardTypeLabel(board.type) }}
          </span>
          <span
            v-if="board.type === 'partner' && board.capacity_hours"
            class="tl-board-capacity"
          >
            {{
              $t("productionPriority.capacityOf", {
                used: board.used_hours,
                total: board.capacity_hours,
              })
            }}
          </span>
          <span class="tl-board-count">{{ localRows(board).length }}</span>
        </div>

        <div
          ref="timelineScroll"
          class="tl-board-scroll"
          :class="{ 'is-dragging': timelineDrag }"
          @pointerdown="startTimelineDrag($event)"
          @pointermove="moveTimelineDrag($event)"
          @pointerup="endTimelineDrag($event)"
          @pointercancel="endTimelineDrag($event)"
          @scroll="syncTimelineScroll($event)"
        >
          <div class="tl-board-grid" :style="timelineStyle">
          <div class="tl-month-row">
            <div class="tl-month-spacer"></div>
            <div
              v-for="month in timelineMonths"
              :key="month.key"
              class="tl-month-cell"
              :style="{ gridColumn: `span ${month.days}` }"
            >
              {{ month.label }}
            </div>
          </div>
          <div class="tl-row tl-header-row">
            <div class="tl-label-cell tl-header tl-col-income">
              {{ $t("productionPriority.income") }}
            </div>
            <div class="tl-label-cell tl-header tl-col-deadline">
              {{ $t("productionPriority.deadline") }}
            </div>
            <div class="tl-label-cell tl-header tl-col-priority">
              {{ $t("productionPriority.priority") }}
            </div>
            <div
              v-for="d in days"
              :key="'h-' + d"
              class="tl-day-cell tl-header"
              :class="[dayClass(d), dayHeaderClass(board, d)]"
              :title="dayHeaderTitle(board, d)"
            >
              <div class="tl-day-weekday">{{ weekdayLabel(d) }}</div>
              <div class="tl-day-num">{{ dayNumber(d) }}</div>
              <span
                v-if="dayStatusFor(board, d)"
                class="tl-day-dot"
                :class="`tl-day-dot--${dayStatusFor(board, d).level}`"
              ></span>
            </div>
          </div>

          <draggable
            :list="localRows(board)"
            @change="onRowChange(board, $event)"
            group="production-priority"
            item-key="id"
            class="tl-rows"
            :animation="150"
            handle=".tl-row-handle"
            :filter="dragFilter"
          >
            <template #item="{ element: row }">
              <div
                class="tl-row-wrapper"
                :class="{ 'tl-row-wrapper--expanded': isRowExpanded(row) }"
              >
                <div
                  class="tl-row"
                  :class="{ 'tl-row--scheduled': row.is_scheduled }"
                >
                  <div class="tl-label-cell tl-col-income">
                    <span
                      class="tl-row-handle"
                      :title="$t('productionPriority.drag')"
                      >⠿</span
                    >
                    <button
                      type="button"
                      class="tl-row-toggle"
                      :aria-expanded="isRowExpanded(row)"
                      :title="
                        isRowExpanded(row)
                          ? $t('productionPriority.hideProducts')
                          : $t('productionPriority.showProducts')
                      "
                      @click="toggleRow(row)"
                    >
                      <span class="tl-row-toggle-icon">{{
                        isRowExpanded(row) ? "▾" : "▸"
                      }}</span>
                      <span class="tl-row-toggle-text">
                        {{ row.pretty_id || "#" + row.id }}
                      </span>
                      <span
                        class="tl-row-toggle-count"
                        v-if="row.items?.length"
                      >
                        ({{ row.items.length }})
                      </span>
                    </button>
                  </div>
                  <div class="tl-label-cell tl-col-deadline">
                    <input
                      type="date"
                      class="
                        form-control form-control-sm
                        tl-input tl-deadline-input
                      "
                      :value="row.deadline_date || ''"
                      :key="row.deadline_date || 'empty'"
                      :title="$t('productionPriority.deadline')"
                      @change="saveRow(row, 'deadline', $event.target.value)"
                    />
                  </div>
                  <div class="tl-label-cell tl-col-priority">
                    <select
                      class="tl-priority-badge"
                      :class="`tl-priority-badge--${row.priority || 'normal'}`"
                      :style="priorityStyle(row.priority)"
                      :value="row.priority || 'normal'"
                      :key="row.priority || 'empty'"
                      :title="$t('productionPriority.priority')"
                      @change="saveRow(row, 'priority', $event.target.value)"
                    >
                      <option value="low">
                        {{ $t("productionPriority.priorityLow") }}
                      </option>
                      <option value="normal">
                        {{ $t("productionPriority.priorityNormal") }}
                      </option>
                      <option value="high">
                        {{ $t("productionPriority.priorityHigh") }}
                      </option>
                    </select>
                  </div>
                  <div
                    v-for="d in days"
                    :key="row.id + '-' + d"
                    class="tl-day-cell"
                    :class="dayClass(d)"
                  ></div>
                  <div class="tl-bars">
                    <div
                      v-if="row.deadline_date && deadlineDayIndex(row) >= 0"
                      class="tl-deadline-line"
                      :class="{ 'tl-deadline-line--overdue': isOverdue(row) }"
                      :style="deadlineLineStyle(row)"
                      :title="
                        $t('productionPriority.deadline') +
                        ': ' +
                        row.deadline_date
                      "
                    ></div>
                    <div
                      class="tl-bar"
                      :class="[
                        row.is_scheduled
                          ? 'tl-bar--scheduled'
                          : 'tl-bar--draft',
                        { 'is-overdue': isOverdue(row) },
                      ]"
                      :style="barStyle(row)"
                      :title="barTitle(row)"
                    >
                      {{ barLabel(row) }}
                    </div>
                  </div>
                </div>
                <div v-if="isRowExpanded(row)" class="tl-row-details">
                  <div class="tl-row-details-inner">
                    <div
                      v-for="item in row.items"
                      :key="item.id"
                      class="tl-product"
                    >
                      <div class="tl-product-image">
                        <img
                          v-if="productImageUrl(item.product.image)"
                          :src="productImageUrl(item.product.image)"
                          :alt="item.product.name"
                        />
                        <div v-else class="tl-product-image-placeholder">
                          {{
                            (item.product.name || "?").charAt(0).toUpperCase()
                          }}
                        </div>
                      </div>
                      <div class="tl-product-info">
                        <div class="tl-product-name">
                          {{ item.product.name }}
                        </div>
                        <div class="tl-product-meta">
                          <span
                            v-if="item.product.serial"
                            class="tl-product-serial"
                          >
                            {{ item.product.serial }}
                          </span>
                          <span v-if="item.sku" class="tl-product-sku">
                            {{ item.sku }}
                          </span>
                          <span class="tl-product-qty">
                            × {{ item.quantity }}
                          </span>
                        </div>
                      </div>
                    </div>
                    <div v-if="!row.items?.length" class="tl-product-empty">
                      {{ $t("productionPriority.noProducts") }}
                    </div>
                  </div>
                </div>
              </div>
            </template>
          </draggable>

            <div v-if="!localRows(board).length" class="tl-empty">
              {{ $t("productionPriority.noRows") }}
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import dayjs from "dayjs";
import draggable from "vuedraggable";
import http from "@/modules/http";
import tenant from "@/modules/tenant";

export default {
  name: "ProductionTimeline",
  components: { draggable },
  props: {
    days: { type: Array, default: () => [] },
    boards: { type: Array, default: () => [] },
  },
  data() {
    return {
      localBoards: [],
      expandedRowIds: new Set(),
      syncingTimelineScroll: false,
      timelineDrag: null,
    };
  },
  computed: {
    timelineStyle() {
      return {
        "--tl-days": this.days.length,
      };
    },
    dragFilter() {
      return ".tl-row--scheduled";
    },
    today() {
      return dayjs().format("YYYY-MM-DD");
    },
    mediaServer() {
      return tenant.getAppMediaUrl ? tenant.getAppMediaUrl() : "";
    },
    timelineMonths() {
      const months = [];
      this.days.forEach((date) => {
        const value = dayjs(date);
        const key = value.format("YYYY-MM");
        const current = months[months.length - 1];
        if (current && current.key === key) {
          current.days += 1;
          return;
        }
        months.push({
          key,
          days: 1,
          label: new Intl.DateTimeFormat(undefined, {
            month: "long",
            year: "numeric",
          }).format(value.toDate()),
        });
      });
      return months;
    },
  },
  watch: {
    boards: {
      immediate: true,
      handler(boards) {
        this.syncLocalBoards(boards);
        const validIds = new Set(
          (boards || []).flatMap((b) => (b.rows || []).map((r) => r.id))
        );
        const next = new Set();
        this.expandedRowIds.forEach((id) => {
          if (validIds.has(id)) next.add(id);
        });
        this.expandedRowIds = next;
      },
    },
  },
  created() {
    this.localBoards = [];
    this.syncLocalBoards(this.boards || []);
  },
  methods: {
    syncLocalBoards(boards) {
      // Build a map of incoming boards by column_key.
      const incoming = new Map();
      (boards || []).forEach((b) => {
        incoming.set(b.column_key, b.rows || []);
      });

      // For each existing local board, replace its rows in place so that
      // vuedraggable (which holds the array reference internally) sees the
      // updated items.
      this.localBoards.forEach((localBoard) => {
        const newRows = incoming.get(localBoard.column_key);
        if (newRows === undefined) {
          // Board removed: clear its rows in place
          localBoard.rows.splice(0, localBoard.rows.length);
          return;
        }
        const oldRows = localBoard.rows;
        // Replace items in place: remove extras, then patch existing items
        // and add new ones.
        while (oldRows.length > newRows.length) {
          oldRows.pop();
        }
        for (let i = 0; i < newRows.length; i++) {
          if (i < oldRows.length) {
            // Patch in place to keep the reference stable for vuedraggable.
            // Use $set/$delete so Vue 2 observes every field change and
            // re-renders the row content (deadline/priority inputs included).
            const target = oldRows[i];
            const source = newRows[i];
            Object.keys(target).forEach((k) => {
              if (!(k in source)) this.$delete(target, k);
            });
            Object.keys(source).forEach((k) => {
              this.$set(target, k, source[k]);
            });
          } else {
            oldRows.push(newRows[i]);
          }
        }
      });

      // For any incoming boards that don't exist locally yet, append them.
      (boards || []).forEach((b) => {
        if (!this.localBoards.some((lb) => lb.column_key === b.column_key)) {
          this.localBoards.push({
            column_key: b.column_key,
            rows: (b.rows || []).slice(),
          });
        }
      });
    },
    localRows(board) {
      const found = this.localBoards.find(
        (b) => b.column_key === board.column_key
      );
      return found ? found.rows : [];
    },
    startTimelineDrag(event) {
      if (event.button !== 0) return;
      if (event.target.closest("button, input, select, a, .tl-row-handle, .tl-bar")) {
        return;
      }

      const scroller = event.currentTarget;
      if (scroller.scrollWidth <= scroller.clientWidth) return;

      this.timelineDrag = {
        scroller,
        pointerId: event.pointerId,
        startX: event.clientX,
        startScrollLeft: scroller.scrollLeft,
      };
      scroller.setPointerCapture?.(event.pointerId);
    },
    moveTimelineDrag(event) {
      if (!this.timelineDrag || event.pointerId !== this.timelineDrag.pointerId) {
        return;
      }
      this.timelineDrag.scroller.scrollLeft =
        this.timelineDrag.startScrollLeft -
        (event.clientX - this.timelineDrag.startX);
      event.preventDefault();
    },
    endTimelineDrag(event) {
      if (!this.timelineDrag || event.pointerId !== this.timelineDrag.pointerId) {
        return;
      }
      this.timelineDrag.scroller.releasePointerCapture?.(event.pointerId);
      this.timelineDrag = null;
    },
    syncTimelineScroll(event) {
      if (this.syncingTimelineScroll) return;

      const source = event.currentTarget;
      const scrollers = Array.isArray(this.$refs.timelineScroll)
        ? this.$refs.timelineScroll
        : [this.$refs.timelineScroll];
      this.syncingTimelineScroll = true;
      scrollers.forEach((scroller) => {
        if (scroller && scroller !== source) {
          scroller.scrollLeft = source.scrollLeft;
        }
      });
      this.$nextTick(() => {
        this.syncingTimelineScroll = false;
      });
    },
    isRowExpanded(row) {
      return this.expandedRowIds.has(row.id);
    },
    toggleRow(row) {
      const next = new Set(this.expandedRowIds);
      if (next.has(row.id)) {
        next.delete(row.id);
      } else {
        next.add(row.id);
      }
      this.expandedRowIds = next;
    },
    productImageUrl(image) {
      if (!image?.path || !image?.name) return null;
      return (
        (this.mediaServer || "") +
        "/" +
        image.path +
        "/262-" +
        image.name +
        ".webp"
      );
    },
    priorityStyle(priority) {
      const map = {
        low: { bg: "#f1f3f5", color: "#6c757d", border: "#dee2e6" },
        normal: { bg: "#cfe2ff", color: "#084298", border: "#9ec5fe" },
        high: { bg: "#f8d7da", color: "#842029", border: "#f1aeb5" },
      };
      const c = map[priority] || map.normal;
      return {
        backgroundColor: c.bg,
        color: c.color,
        borderColor: c.border,
      };
    },
    boardTypeLabel(type) {
      if (type === "warehouse") return this.$t("productionPriority.warehouse");
      if (type === "partner") return this.$t("productionPriority.partner");
      return this.$t("productionPriority.unassigned");
    },
    boardBadgeClass(type) {
      if (type === "partner") return "bg-info";
      if (type === "warehouse") return "bg-secondary";
      return "bg-light text-dark";
    },
    onRowChange(board, event) {
      const rows = this.localRows(board);
      if (event.added) {
        const row = event.added.element;
        const newIndex =
          event.added.newIndex !== undefined
            ? event.added.newIndex
            : rows.findIndex((r) => r.id === row.id);
        http
          .fetch("/production/priority/move", {
            operation_id: row.id,
            location_type: board.type,
            location_id: board.id,
            new_index: newIndex,
          })
          .then(() => this.$emit("reload"))
          .catch(() => {
            this.$emit("reload");
          });
      } else if (event.moved) {
        http
          .fetch("/production/priority/reorder", {
            location_type: board.type,
            location_id: board.id,
            ordered_ids: rows.map((r) => r.id),
          })
          .then(() => this.$emit("reload"))
          .catch(() => {
            this.$emit("reload");
          });
      }
    },
    saveRow(row, field, value) {
      const payload = { operation_id: row.id };
      if (field === "deadline") {
        payload.deadline_date = value || "";
      } else {
        payload.priority = value === "" || value == null ? "" : value;
      }
      http
        .fetch("/production/priority/operation", payload)
        .then(() => this.$emit("reload"))
        .catch(() => {
          this.$emit("reload");
        });
    },
    weekdayLabel(date) {
      return dayjs(date).format("ddd");
    },
    dayNumber(date) {
      return dayjs(date).format("D");
    },
    isWeekend(date) {
      const day = dayjs(date).day();
      return day === 0 || day === 6;
    },
    isToday(date) {
      return date === this.today;
    },
    dayClass(date) {
      return {
        "is-weekend": this.isWeekend(date),
        "is-today": this.isToday(date),
      };
    },
    dayStatusFor(board, day) {
      const list = board && board.day_status;
      if (!Array.isArray(list)) return null;
      return list.find((d) => d.day === day) || null;
    },
    dayHeaderClass(board, day) {
      const ds = this.dayStatusFor(board, day);
      if (!ds) return {};
      return {
        [`tl-day-${ds.level}`]: true,
      };
    },
    dayHeaderTitle(board, day) {
      const ds = this.dayStatusFor(board, day);
      if (!ds) return "";
      const lines = [day];
      if (ds.holiday) {
        lines.push(
          this.$t("productionPriority.dayHoliday") + ": " + ds.holiday.name
        );
      }
      lines.push(
        this.$t("productionPriority.teamCount", { count: ds.team_count })
      );
      if (ds.leaves.length) {
        const names = ds.leaves
          .map((l) => {
            const reason =
              l.reason && l.reason.name ? " (" + l.reason.name + ")" : "";
            return (l.name || "?") + reason;
          })
          .join(", ");
        lines.push(
          this.$t("productionPriority.leavesCount", {
            count: ds.leaves.length,
          }) +
            ": " +
            names
        );
      }
      return lines.join("\n");
    },
    isOverdue(order) {
      if (!order.deadline_date) return false;
      return dayjs(order.deadline_date).isBefore(dayjs(), "day");
    },
    barStyle(order) {
      const total = this.days.length;
      if (!total) return { display: "none" };
      const s = this.days.indexOf(order.start_date);
      const e = this.days.indexOf(order.end_date);
      if (s < 0 || e < 0) return { display: "none" };
      const start = Math.max(0, Math.min(s, total - 1));
      const end = Math.max(start, Math.min(e, total - 1));
      return {
        left: (start / total) * 100 + "%",
        width: ((end - start + 1) / total) * 100 + "%",
      };
    },
    deadlineDayIndex(order) {
      if (!order.deadline_date) return -1;
      return this.days.indexOf(order.deadline_date);
    },
    deadlineLineStyle(order) {
      const total = this.days.length;
      if (!total) return { display: "none" };
      const idx = this.deadlineDayIndex(order);
      if (idx < 0) return { display: "none" };
      const left = (idx / total) * 100;
      return {
        left: `calc(${left}% + ${(1 / total) * 100}% )`,
      };
    },
    barLabel(order) {
      return order.pretty_id || "#" + order.id;
    },
    barTitle(order) {
      const lines = [
        order.pretty_id || "#" + order.id,
        order.estimated_hours + "h",
        order.start_date +
          (order.end_date !== order.start_date ? " → " + order.end_date : ""),
      ];
      if (order.deadline_date) {
        lines.push("deadline: " + order.deadline_date);
      }
      order.items.forEach((item) => {
        lines.push(
          (item.product?.name || item.sku || "item") + " × " + item.quantity
        );
      });
      return lines.join("\n");
    },
  },
};
</script>

<style scoped>
.production-timeline {
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
}
.timeline-legend {
  display: flex;
  align-items: center;
  gap: 16px;
  font-size: 12px;
  color: #6c757d;
  padding: 0 4px 8px;
}
.legend-item {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.legend-dot {
  display: inline-block;
  width: 14px;
  height: 10px;
  border-radius: 2px;
}
.timeline-scroll {
  flex: 1;
  width: 100%;
  min-width: 0;
  max-width: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding-bottom: 8px;
}
.tl-board {
  margin-bottom: 20px;
  width: 100%;
  min-width: 0;
}
.tl-board-header {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 10px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-bottom: none;
  border-radius: 8px 8px 0 0;
  box-sizing: border-box;
  min-width: 100%;
}
.tl-board-name {
  font-weight: 600;
  font-size: 14px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tl-board-capacity {
  font-size: 12px;
  color: #6c757d;
}
.tl-board-count {
  margin-left: auto;
  font-size: 12px;
  color: #6c757d;
  background: #f4f5f7;
  border-radius: 10px;
  padding: 0 8px;
  line-height: 18px;
}
.tl-board-grid {
  --tl-income: 240px;
  --tl-deadline: 140px;
  --tl-priority: 90px;
  --tl-label: calc(var(--tl-income) + var(--tl-deadline) + var(--tl-priority));
  display: block;
  width: max-content;
  min-width: 100%;
}
.tl-board-scroll {
  width: 100%;
  min-width: 0;
  overflow-x: auto;
  overflow-y: hidden;
  scrollbar-gutter: stable;
  cursor: grab;
}
.tl-board-scroll.is-dragging {
  cursor: grabbing;
  user-select: none;
}
.tl-month-row {
  display: grid;
  grid-template-columns:
    var(--tl-income)
    var(--tl-deadline)
    var(--tl-priority)
    repeat(var(--tl-days), minmax(30px, 1fr));
  min-height: 30px;
  border: 1px solid #dee2e6;
  border-bottom: none;
  background: #f1f3f5;
  position: sticky;
  top: 0;
  z-index: 5;
}
.tl-month-spacer {
  grid-column: span 3;
  border-right: 1px solid #dee2e6;
  position: sticky;
  left: 0;
  z-index: 6;
  background: #f1f3f5;
}
.tl-month-cell {
  min-width: 0;
  padding: 7px 10px 6px;
  border-right: 1px solid #dee2e6;
  color: #495057;
  font-size: 12px;
  font-weight: 700;
  letter-spacing: 0.2px;
  text-transform: capitalize;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tl-row {
  position: relative;
  display: grid;
  grid-template-columns:
    var(--tl-income)
    var(--tl-deadline)
    var(--tl-priority)
    repeat(var(--tl-days), minmax(30px, 1fr));
  border-bottom: 1px solid #e9ecef;
}
.tl-header-row {
  border-bottom: 1px solid #dee2e6;
  position: sticky;
  top: 30px;
  z-index: 4;
}
.tl-label-cell {
  background: #fff;
  border-right: 1px solid #dee2e6;
  padding: 6px 10px;
  min-height: 44px;
  display: flex;
  align-items: center;
  position: relative;
  z-index: 2;
}
.tl-row > .tl-col-income,
.tl-header-row > .tl-col-income {
  position: sticky;
  left: 0;
  z-index: 6;
}
.tl-row > .tl-col-deadline,
.tl-header-row > .tl-col-deadline {
  position: sticky;
  left: var(--tl-income);
  z-index: 6;
}
.tl-row > .tl-col-priority,
.tl-header-row > .tl-col-priority {
  position: sticky;
  left: calc(var(--tl-income) + var(--tl-deadline));
  z-index: 6;
}
.tl-row > .tl-col-income,
.tl-row > .tl-col-deadline,
.tl-row > .tl-col-priority {
  background: #fff;
}
.tl-row-wrapper--expanded > .tl-row > .tl-col-income,
.tl-row-wrapper--expanded > .tl-row > .tl-col-deadline,
.tl-row-wrapper--expanded > .tl-row > .tl-col-priority {
  background: #f0f7ff;
}
.tl-label-cell.tl-header {
  background: #f8f9fa;
  font-size: 11px;
  color: #6c757d;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.3px;
}
.tl-col-income {
  gap: 8px;
}
.tl-col-deadline,
.tl-col-priority {
  padding: 4px 8px;
}
.tl-row-handle {
  cursor: grab;
  color: #adb5bd;
  font-size: 14px;
  line-height: 1;
  padding: 0 2px;
  user-select: none;
  flex-shrink: 0;
}
.tl-row--scheduled .tl-row-handle {
  cursor: not-allowed;
  opacity: 0.4;
}
.tl-row-handle:active {
  cursor: grabbing;
}
.tl-income {
  min-width: 0;
}
.tl-income-id {
  font-size: 13px;
  font-weight: 600;
}
.tl-row-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: 1px solid transparent;
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 13px;
  font-weight: 600;
  color: #212529;
  cursor: pointer;
  text-align: left;
  min-width: 0;
  max-width: 100%;
}
.tl-row-toggle:hover {
  background: #f1f3f5;
  border-color: #dee2e6;
}
.tl-row-toggle:focus {
  outline: none;
  border-color: #0d6efd;
  background: #f1f3f5;
}
.tl-row-toggle-icon {
  color: #6c757d;
  font-size: 11px;
  line-height: 1;
  width: 10px;
  text-align: center;
  flex-shrink: 0;
}
.tl-row-toggle-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tl-row-toggle-count {
  color: #6c757d;
  font-weight: 400;
  font-size: 11px;
  flex-shrink: 0;
}
.tl-row-details {
  background: #f8f9fa;
  border: 1px solid #e9ecef;
  border-top: none;
  padding: 10px 12px;
}
.tl-row-details-inner {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-left: var(--tl-label);
}
.tl-product {
  display: flex;
  align-items: center;
  gap: 10px;
  background: #fff;
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 6px 10px 6px 6px;
  min-width: 240px;
  max-width: 360px;
  flex: 1 1 240px;
}
.tl-product-image {
  width: 48px;
  height: 48px;
  flex-shrink: 0;
  border-radius: 4px;
  overflow: hidden;
  background: #f1f3f5;
  display: flex;
  align-items: center;
  justify-content: center;
}
.tl-product-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.tl-product-image-placeholder {
  font-size: 18px;
  font-weight: 600;
  color: #adb5bd;
}
.tl-product-info {
  min-width: 0;
  flex: 1;
}
.tl-product-name {
  font-size: 13px;
  font-weight: 600;
  color: #212529;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.tl-product-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 4px 8px;
  font-size: 11px;
  color: #6c757d;
  margin-top: 2px;
}
.tl-product-serial,
.tl-product-sku {
  background: #f1f3f5;
  padding: 1px 6px;
  border-radius: 3px;
  font-family: monospace;
}
.tl-product-qty {
  font-weight: 600;
  color: #495057;
}
.tl-product-empty {
  font-size: 12px;
  color: #adb5bd;
  font-style: italic;
}
.tl-row-wrapper--expanded > .tl-row {
  background: #f0f7ff;
}
.tl-qty {
  color: #6c757d;
}
.tl-input {
  min-width: 0;
}
.tl-col-priority .tl-input option[value="low"] {
  color: #6c757d;
}
.tl-col-priority .tl-input option[value="normal"] {
  color: #0d6efd;
}
.tl-col-priority .tl-input option[value="high"] {
  color: #dc3545;
  font-weight: 600;
}
.tl-col-priority .tl-input {
  font-weight: 600;
}
.tl-day-cell {
  min-height: 34px;
  border-right: 1px solid #f1f3f5;
}
.tl-day-cell.is-weekend:not(.tl-header) {
  background: #fafafa;
}
.tl-day-cell.is-today:not(.tl-header) {
  background: #eaf3ff;
}
.tl-header {
  background: #f8f9fa;
  text-align: center;
  padding: 4px 0;
  font-size: 11px;
  color: #6c757d;
  line-height: 1.2;
}
.tl-header.is-today {
  background: #e7f1ff;
}
.tl-bars {
  position: absolute;
  top: 0;
  bottom: 0;
  left: var(--tl-label);
  right: 0;
}
.tl-deadline-line {
  position: absolute;
  top: 0;
  bottom: 0;
  width: 2px;
  background: #dc3545;
  z-index: 3;
  pointer-events: auto;
  box-shadow: 0 0 0 1px rgba(220, 53, 69, 0.15);
}
.tl-deadline-line--overdue {
  background: #dc3545;
  box-shadow: 0 0 0 1px rgba(220, 53, 69, 0.35);
  animation: tl-deadline-pulse 1.6s ease-in-out infinite;
}
@keyframes tl-deadline-pulse {
  0%,
  100% {
    opacity: 1;
  }
  50% {
    opacity: 0.5;
  }
}
.tl-bar {
  position: absolute;
  top: 6px;
  height: calc(100% - 12px);
  min-height: 22px;
  border-radius: 4px;
  padding: 0 6px;
  font-size: 11px;
  line-height: 22px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  box-sizing: border-box;
}
.tl-bar--scheduled {
  background: #0d6efd;
  color: #fff;
  border: 1px solid #0b5ed7;
}
.tl-bar--draft {
  background: #fff3cd;
  color: #664d03;
  border: 1px solid #ffca2c;
}
.tl-bar.is-overdue {
  border-left: 3px solid #dc3545;
}
.tl-empty {
  border: 1px dashed #e0e0e0;
  border-top: none;
  padding: 12px;
  font-size: 12px;
  color: #adb5bd;
  text-align: center;
}
.tl-day-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-top: 2px;
  flex-shrink: 0;
  box-shadow: 0 0 0 1px rgba(0, 0, 0, 0.08);
}
.tl-day-dot--all {
  background: #28a745;
}
.tl-day-dot--some {
  background: #ffc107;
}
.tl-day-dot--none {
  background: #dc3545;
}
.tl-day-dot--holiday {
  background: #6f42c1;
}
.tl-header .tl-day-cell {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 1px;
}
.tl-day-cell.tl-day-holiday {
  background: rgba(111, 66, 193, 0.08);
}
.tl-day-cell.tl-day-none {
  background: rgba(220, 53, 69, 0.08);
}
.tl-day-cell.tl-day-some {
  background: rgba(255, 193, 7, 0.08);
}
.tl-priority-badge {
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  border: 1px solid transparent;
  border-radius: 999px;
  padding: 2px 22px 2px 10px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.4;
  text-transform: uppercase;
  letter-spacing: 0.3px;
  cursor: pointer;
  width: 100%;
  max-width: 100px;
  background-image: url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 8 8'%3E%3Cpath fill='%236c757d' d='M0 2l4 4 4-4z'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: right 6px center;
  background-size: 8px 8px;
}
.tl-priority-badge:focus {
  outline: none;
  box-shadow: 0 0 0 2px rgba(13, 110, 253, 0.25);
}
.tl-priority-badge--low {
  background-color: #f1f3f5;
  color: #6c757d;
  border-color: #dee2e6;
}
.tl-priority-badge--normal {
  background-color: #cfe2ff;
  color: #084298;
  border-color: #9ec5fe;
}
.tl-priority-badge--high {
  background-color: #f8d7da;
  color: #842029;
  border-color: #f1aeb5;
}
</style>
